import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  OmschrijvingEnBedrag,
  Onderneming
} from '@snelstart-ventures/fiscaal/domain-logic';
import {debounceTime, distinctUntilChanged, filter, map, Observable, shareReplay, Subscription, take, tap} from 'rxjs';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-egalisatiereserve',
  templateUrl: './spec-egalisatiereserve.component.html',
  imports: [
    SharedUiButtonModule,
    SharedUiDialogModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    CommonModule
  ],
  styleUrls: ['./spec-egalisatiereserve.component.scss']
})
export class SpecEgalisatiereserveComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private bedragSubscriptions = new Subscription();
  public indexOnderneming = 0;
  public dialogVisible = false;
  public isPartner = false;

  public formGroup = new FormGroup({
    waardeOpening: new FormControl<number | null>(null),
    waardeSlot: new FormControl<number | null>(null),
    dotatieEgalisatiereserve: new FormControl<number | null>(null),
    vrijvalEgalisatiereserve: new FormControl<number | null>(null),
  })

  public omschrijvingBedragFg = new FormGroup({
    omschrijvingBedrag: new FormArray<
      FormGroup<{
        omschrijving: FormControl<string | null>;
        bedrag: FormControl<number | null>;
      }>
    >([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }

  showDialog() {
    this.handleSwitch(this.dataService.incomeTaxEntity.value, this.isPartner);
    this.dialogVisible = true;
  }

  get omschrijvingBedrag(): FormArray<FormGroup<{ omschrijving: FormControl<string | null>; bedrag: FormControl<number | null>; }>> {
    return this.omschrijvingBedragFg.controls.omschrijvingBedrag;
  }

  addRij(): void {
    this.omschrijvingBedrag.push(this.createFormGroup(), { emitEvent: false });
    this.watchBedragChanges();
  }

  verwijderRij(index: number): void {
    this.omschrijvingBedrag.removeAt(index, { emitEvent: false });
    this.saveForm();
    this.totaalBedrag;
    this.totaalVrijval;
    this.watchBedragChanges();
  }

  createFormGroup(item?: OmschrijvingEnBedrag) {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null, Validators.required),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null, Validators.required),
    });
  }

  private watchBedragChanges(): void {
    this.bedragSubscriptions.unsubscribe();
    this.bedragSubscriptions = new Subscription();

    this.omschrijvingBedrag.controls.forEach(group => {
      this.bedragSubscriptions.add(
        group.controls.bedrag.valueChanges.pipe(
          debounceTime(300),
          map(() => this.saveForm()),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
          this.totaalBedrag;
          this.totaalVrijval;
        })
      );
    });
  }

  get totaalBedrag(): number {
    const sum = this.omschrijvingBedrag.controls
      .map(group => group.controls.bedrag.value ?? 0)
      .reduce((sum, val) => sum + val, 0);
    this.formGroup.patchValue({dotatieEgalisatiereserve: sum}, {emitEvent: false});
    return sum;
  }

  get totaalVrijval(): any {
    const opening = this.formGroup.get('waardeOpening')?.value ?? 0;
    const dotatie = this.formGroup.get('dotatieEgalisatiereserve')?.value ?? 0;
    const slot = this.formGroup.get('waardeSlot')?.value ?? 0;
    const vrijval = (opening + dotatie - slot);
    this.formGroup.patchValue({vrijvalEgalisatiereserve: vrijval}, {emitEvent: false});
    return vrijval;
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.resetFormGroup();
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchFormGroups(ondernemingen);
  }

  private resetFormGroup() {
    this.formGroup.reset({}, {emitEvent: false})
  }

  private patchFormGroups(ondernemingen: Onderneming[]) {
    this.formGroup.reset({}, {emitEvent: false});
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    this.formGroup.patchValue({dotatieEgalisatiereserve: onderneming.dotatieEgalisatiereserve ?? 0}, {emitEvent: false});
    this.formGroup.patchValue({vrijvalEgalisatiereserve: onderneming.vrijvalEgalisatiereserve}, {emitEvent: false});
    if (onderneming.eigenVermogen?.at(1)) {
      this.formGroup.patchValue({waardeOpening : onderneming.eigenVermogen[1].waardeOpening}, {emitEvent: false});
      this.formGroup.patchValue({waardeSlot : onderneming.eigenVermogen[1].waardeSlot}, {emitEvent: false});
    }
    // Only reset and repopulate the FormArray if data is present
    if (onderneming.beginstandEgalisatiereserve && onderneming.beginstandEgalisatiereserve.length > 0) {
      this.omschrijvingBedragFg.reset({}, {emitEvent: false});
      this.updateForm(onderneming.beginstandEgalisatiereserve);
    }
    this.totaalBedrag;
  }

  ngOnDestroy(): void {
    this.bedragSubscriptions.unsubscribe();
  }

  private updateForm(omschrijvingEnBedrags: OmschrijvingEnBedrag[]): void {
    const lijst = omschrijvingEnBedrags ?? [];
    const formArray = this.omschrijvingBedrag;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, { emitEvent: false });
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], { emitEvent: false });
      } else {
        formArray.push(this.createFormGroup(lijst[i]), { emitEvent: false });
      }
    }
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();
    const formLijst = this.omschrijvingBedragFg.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};

        // Clone or create the array
        const egalisatiereserve = Array.isArray(onderneming.eigenVermogen)
          ? [...onderneming.eigenVermogen]
          : [];
        // Ensure the array has at least 2 items
        while (egalisatiereserve.length < 2) {
          egalisatiereserve.push({});
        }
        // Update the second item
        egalisatiereserve[1] = formValue;

        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          dotatieEgalisatiereserve: (formValue.dotatieEgalisatiereserve ?? 0) > 1 ? formValue.dotatieEgalisatiereserve : undefined,
          vrijvalEgalisatiereserve: (formValue.vrijvalEgalisatiereserve ?? 0) > 1 ? formValue.vrijvalEgalisatiereserve : undefined,
          beginstandEgalisatiereserve: formLijst.omschrijvingBedrag.length > 0 ? formLijst.omschrijvingBedrag : undefined,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
          ...currentEntity.aangifte,
          partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
        }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }
}
